import SignClient from '@walletconnect/sign-client';
import { WalletConnectModal } from '@walletconnect/modal';
import QRCode from 'qrcode';

const TRON_CHAINS = {
  mainnet: 'tron:0x2b6653dc',
  nile: 'tron:0xcd8690dc',
  shasta: 'tron:0x94a9059e',
};

class TronWalletConnect {
  constructor(projectId, network = 'nile') {
    this.projectId = projectId;
    this.network = network;
    this.chain = TRON_CHAINS[network] || TRON_CHAINS.nile;
    this.client = null;
    this.modal = null;
    this.session = null;
    this.onDisconnect = null;
  }

  async init() {
    this.client = await SignClient.init({
      projectId: this.projectId,
      metadata: {
        name: 'TRON USDT Payments',
        description: 'TRON USDT Payment System',
        url: window.location.origin,
        icons: [window.location.origin + '/favicon.ico'],
      },
    });

    this.modal = new WalletConnectModal({
      projectId: this.projectId,
      chains: [this.chain],
      themeMode: 'dark',
      themeVariables: {
        '--wcm-z-index': '10000',
        '--wcm-accent-color': '#ff060a',
      },
    });

    this.client.on('session_delete', () => {
      this.session = null;
      if (this.onDisconnect) this.onDisconnect();
    });

    this.client.on('session_expire', () => {
      this.session = null;
      if (this.onDisconnect) this.onDisconnect();
    });

    const sessions = this.client.session.getAll();
    const tronSession = sessions.find(s => s.namespaces.tron);
    if (tronSession) this.session = tronSession;
  }

  async connect({ showModal = false } = {}) {
    const { uri, approval } = await this.client.connect({
      requiredNamespaces: {
        tron: {
          chains: [this.chain],
          methods: ['tron_signTransaction', 'tron_signMessage'],
          events: [],
        },
      },
    });

    if (!uri) throw new Error('WalletConnect: no URI');

    if (showModal) {
      await this.modal.openModal({ uri });
    }

    return {
      uri,
      waitForApproval: () => approval().then(s => {
        if (showModal) this.modal.closeModal();
        this.session = s;
        return s;
      }).catch(e => {
        if (showModal) this.modal.closeModal();
        throw e;
      }),
    };
  }

  closeModal() {
    if (this.modal) this.modal.closeModal();
  }

  get address() {
    if (!this.session) return null;
    const accounts = this.session.namespaces.tron?.accounts || [];
    return accounts[0]?.split(':').pop() || null;
  }

  get connected() {
    return !!this.session && !!this.address;
  }

  async signTransaction(transaction) {
    if (!this.session) throw new Error('WalletConnect: не подключён');
    const v1 = this.session.sessionProperties?.tron_method_version === 'v1';
    const params = v1
      ? { address: this.address, transaction }
      : { address: this.address, transaction: { transaction } };
    const result = await this.client.request({
      topic: this.session.topic,
      chainId: this.chain,
      request: { method: 'tron_signTransaction', params },
    });
    return result?.result ?? result;
  }

  async disconnect() {
    if (this.session) {
      try {
        await this.client.disconnect({
          topic: this.session.topic,
          reason: { code: 6000, message: 'User disconnected' },
        });
      } catch {}
      this.session = null;
    }
  }

  static async generateQR(uri) {
    return QRCode.toDataURL(uri, {
      width: 280,
      margin: 2,
      color: { dark: '#e6edf3', light: '#161b25' },
    });
  }
}

window.TronWalletConnect = TronWalletConnect;
